// frontend/src/components/FilterContainer.js
import React from 'react';
import { getOwnershipOptions } from '../utils/statusOptions';
import TagFilterInput from './TagFilterInput';
import {
  CONDITION_OPTIONS,
  PLATFORM_OPTIONS,
  PLATFORM_PLAYED_OPTIONS,
  ANIME_MEDIA_TYPE_OPTIONS,
  FORMAT_OPTIONS,
  SEASON_OPTIONS,
  EXPLICIT_OPTIONS,
  STATUS_ICONS,
  OWNERSHIP_ICONS,
  getPresentTypes,
  getAllTagValues,
  countMatching,
} from '../utils/filterEngine';

const FilterContainer = ({ statusOptions, filters, onFilterChange, visibleItems, allItems, canViewExplicit }) => {
  // `allItems` (unfiltered) decides which filter groups exist; `visibleItems` (post-filter)
  // is only used for the counts next to each option, so groups don't disappear just because
  // the current selection happens to exclude every matching item.
  const presentTypes = getPresentTypes(allItems && allItems.length ? allItems : visibleItems);

  const hasAnime = presentTypes.includes('Anime');
  const hasScreenMedia = presentTypes.includes('ScreenMedia');
  const hasSteam = presentTypes.includes('SteamGame');
  const physicalPlatforms = new Set(
    (allItems || []).filter((i) => i.type === 'PhysicalGame').map((i) => i.platform).filter(Boolean)
  );
  const showPlatformFilter = physicalPlatforms.size > 1;
  const ownershipOptions = getOwnershipOptions(presentTypes);
  const showCondition = presentTypes.some((t) => ['PhysicalGame', 'Music', 'Manga', 'Book'].includes(t));
  const allTagValues = getAllTagValues(allItems);
  const selectedTags = filters.genre_tag || [];

  const countItems = (field, value) => countMatching(visibleItems, field, value);

  const handleCheckboxChange = (type, value) => {
    const checked = !(filters[type]?.includes(value));
    onFilterChange(type, value, checked);
  };

  // Renders a single checkbox row. The icon slot is always present (even when empty) so every
  // row's checkbox/icon/text/count columns line up, regardless of whether that particular option
  // has an icon or how long its label/count are.
  const renderOption = ({ filterKey, value, icon, label }) => (
    <label key={`${filterKey}-${value}`} className="filter-label">
      <input
        type="checkbox"
        className="filter-checkbox"
        checked={filters[filterKey]?.includes(value) || false}
        onChange={() => handleCheckboxChange(filterKey, value)}
      />
      <span className="filter-icon-slot">
        {icon && <img src={icon} className="filter-icon icon" alt="" />}
      </span>
      <span className="filter-label-text">{label}</span>
      <span className="filter-count">{countItems(filterKey, value)}</span>
    </label>
  );

  const renderCustomGroup = (title, items) => (
    <div className="filter-group" key={title}>
      <h3>{title}</h3>
      {items.map((item) => renderOption(item))}
    </div>
  );

  const renderGroup = (title, options, filterKey, valueOf = (o) => o.value, labelOf = (o) => o.label ?? o) =>
    renderCustomGroup(
      title,
      options.map((option) => ({
        filterKey,
        value: valueOf(option),
        icon: option.icon,
        label: labelOf(option),
      }))
    );

  return (
    <div className="filter-container">
      {allTagValues.length > 0 && (
        <div className="filter-group">
          <h3>Genre / Tags</h3>
          <TagFilterInput
            allItems={allItems}
            visibleItems={visibleItems}
            selected={selectedTags}
            onAdd={(value) => onFilterChange('genre_tag', value, true)}
            onRemove={(value) => onFilterChange('genre_tag', value, false)}
          />
        </div>
      )}

      {renderGroup(
        'Favorite',
        [{ value: 'true', label: 'Favorited', icon: '/static/icons/star-solid.svg' }],
        'favorite'
      )}

      {statusOptions.length > 0 &&
        renderGroup(
          'Status',
          statusOptions.map((s) => ({ value: s, label: s, icon: STATUS_ICONS[s] })),
          'status'
        )}

      {(hasAnime || hasScreenMedia) && (
        <>
          {hasAnime && renderGroup('Media Type', ANIME_MEDIA_TYPE_OPTIONS, 'media_type')}
          {hasScreenMedia && renderGroup('Format', FORMAT_OPTIONS, 'is_animated')}
          {renderGroup('Seasons', SEASON_OPTIONS, 'seasons')}
        </>
      )}

      {ownershipOptions.length > 0 &&
        renderGroup(
          'Ownership',
          ownershipOptions.map((o) => ({ value: o, label: o, icon: OWNERSHIP_ICONS[o] })),
          'ownership_status'
        )}

      {showCondition && renderGroup('Condition', CONDITION_OPTIONS, 'condition')}

      {showPlatformFilter && renderGroup('Platform', PLATFORM_OPTIONS, 'platform')}

      {hasSteam && (
        <>
          {renderCustomGroup('Achievements', [
            {
              filterKey: 'achievements',
              value: 'has-achievements',
              icon: '/static/icons/trophy-solid.svg',
              label: 'Has Achievements',
            },
            {
              filterKey: 'perfect_game',
              value: 'perfect',
              icon: '/static/icons/medal-solid.svg',
              label: 'Perfect Game',
            },
          ])}
          {renderGroup('Played On', PLATFORM_PLAYED_OPTIONS, 'platform_played')}
        </>
      )}

      {canViewExplicit && renderGroup('Explicit', EXPLICIT_OPTIONS, 'explicit')}
    </div>
  );
};

export default FilterContainer;